import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Column {
  id: string;
  label: string;
  visible: boolean;
  order: number;
}

interface TransactionImportColumnsTabProps {
  readonly columns: Column[];
  readonly visibleColumns: string[];
  readonly columnOrder: string[];
  readonly onVisibleColumnsChange: (columns: string[]) => void;
  readonly onColumnOrderChange: (order: string[]) => void;
}

function SortableColumn({ column, isVisible, onToggle }: Readonly<{ column: Column; isVisible: boolean; onToggle: (id: string, checked: boolean) => void }>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-2 p-2 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-4 w-4 text-slate-400 dark:text-slate-500" />
      </div>
      <Checkbox
        id={`column-${column.id}`}
        checked={isVisible}
        onCheckedChange={(checked) => onToggle(column.id, checked as boolean)}
      />
      <label
        htmlFor={`column-${column.id}`}
        className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer flex-1"
      >
        {column.label}
      </label>
    </div>
  );
}

export function TransactionImportColumnsTab({
  columns,
  visibleColumns,
  columnOrder,
  onVisibleColumnsChange,
  onColumnOrderChange,
}: Readonly<TransactionImportColumnsTabProps>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onColumnOrderChange(newOrder);
    }
  };

  const handleToggle = (columnId: string, checked: boolean) => {
    if (checked) {
      onVisibleColumnsChange([...visibleColumns, columnId]);
    } else {
      onVisibleColumnsChange(visibleColumns.filter((id) => id !== columnId));
    }
  };

  const handleSelectAll = () => {
    if (visibleColumns.length === columns.length) {
      onVisibleColumnsChange([]);
    } else {
      onVisibleColumnsChange(columns.map((c) => c.id));
    }
  };

  // Get ordered columns
  const orderedColumns = columnOrder
    .map((id) => columns.find((c) => c.id === id))
    .filter((c): c is Column => c !== undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Visible Columns ({visibleColumns.length}/{columns.length})
        </Label>
        <button
          onClick={handleSelectAll}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          {visibleColumns.length === columns.length ? 'Hide All' : 'Show All'}
        </button>
      </div>

      <p className="text-xs text-slate-600 dark:text-slate-400">
        Drag to reorder columns, check/uncheck to show/hide
      </p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {orderedColumns.map((column) => (
              <SortableColumn
                key={column.id}
                column={column}
                isVisible={visibleColumns.includes(column.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
